import { makeObservable, observable, runInAction } from 'mobx';
import Dexie from 'dexie';
import { ulid } from 'ulid';
import type { FieldFilter } from './ui-source-filter';
import type { RootStore } from 'logic';

export interface SavedView {
  id: string;
  sourceId: string;
  name: string;
  filters: FieldFilter[];
  filterMode: 'AND' | 'OR';
  searchTerm: string;
  showUnused: boolean;
  isDefault: boolean;
  createdAt: number;
}

export class UiSavedViewsStore {
  viewsBySourceId: Record<string, SavedView[]> = {};
  activeViewId: Record<string, string | null> = {};
  private savedViewsTable: Dexie.Table<SavedView, string>;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      viewsBySourceId: observable,
      activeViewId: observable,
    });

    const db = new Dexie('UiSavedViewsStore');
    db.version(1).stores({
      savedViews: '&id, sourceId',
    });
    this.savedViewsTable = db.table('savedViews');
    this.rootStore = rootStore;
  }

  async init() {
    try {
      const views = await this.savedViewsTable.toArray();
      runInAction(() => {
        for (const view of views) {
          if (!this.viewsBySourceId[view.sourceId]) {
            this.viewsBySourceId[view.sourceId] = [];
          }
          this.viewsBySourceId[view.sourceId].push(view);
        }
      });
    } catch (e) {
      console.warn('[UiSavedViewsStore] Failed to load saved views:', e);
    }
  }

  getViews(sourceId: string): SavedView[] {
    return this.viewsBySourceId[sourceId] || [];
  }

  getActiveViewId(sourceId: string): string | null {
    return this.activeViewId[sourceId] || null;
  }

  async saveView(sourceId: string, name: string): Promise<void> {
    const { uiSourceFilterStore, uiSourceSearchStore } = this.rootStore;

    const view: SavedView = {
      id: ulid(),
      sourceId,
      name,
      filters: uiSourceFilterStore.getFilters(sourceId).map((f) => ({ ...f })),
      filterMode: uiSourceFilterStore.getFilterMode(sourceId),
      searchTerm: uiSourceSearchStore.searchesById[sourceId] || '',
      showUnused: uiSourceSearchStore.showOnlyUnusedRowsById[sourceId] ?? false,
      isDefault: false,
      createdAt: Date.now(),
    };

    const plain = JSON.parse(JSON.stringify(view)) as SavedView;
    try {
      await this.savedViewsTable.put(plain);
    } catch (e) {
      console.warn('[UiSavedViewsStore] Failed to save view:', e);
      throw e;
    }

    runInAction(() => {
      if (!this.viewsBySourceId[sourceId]) {
        this.viewsBySourceId[sourceId] = [];
      }
      this.viewsBySourceId[sourceId].push(plain);
      this.activeViewId[sourceId] = plain.id;
    });
  }

  async applyView(sourceId: string, viewId: string): Promise<void> {
    const views = this.getViews(sourceId);
    const view = views.find((v) => v.id === viewId);
    if (!view) return;

    const { uiSourceFilterStore, uiSourceSearchStore } = this.rootStore;

    runInAction(() => {
      uiSourceFilterStore.clearFilters(sourceId);
      for (const filter of view.filters) {
        uiSourceFilterStore.addFilter(sourceId, filter.fieldId, filter.operator, filter.value);
      }
      uiSourceFilterStore.setFilterMode(sourceId, view.filterMode);
      uiSourceSearchStore.setSearchString(sourceId, view.searchTerm);

      const currentShowUnused = uiSourceSearchStore.showOnlyUnusedRowsById[sourceId] ?? false;
      if (currentShowUnused !== view.showUnused) {
        uiSourceSearchStore.toggleShowOnlyUnusedRows(sourceId);
      }

      this.activeViewId[sourceId] = viewId;
    });
  }

  async deleteView(sourceId: string, viewId: string): Promise<void> {
    try {
      await this.savedViewsTable.delete(viewId);
    } catch (e) {
      console.warn('[UiSavedViewsStore] Failed to delete view:', e);
      throw e;
    }

    runInAction(() => {
      const views = this.viewsBySourceId[sourceId];
      if (views) {
        this.viewsBySourceId[sourceId] = views.filter((v) => v.id !== viewId);
      }
      if (this.activeViewId[sourceId] === viewId) {
        this.activeViewId[sourceId] = null;
      }
    });
  }

  async setDefaultView(sourceId: string, viewId: string | null): Promise<void> {
    const views = this.getViews(sourceId);
    const updatedViews = views.map((view) => {
      const wasDefault = view.isDefault;
      const shouldBeDefault = view.id === viewId;
      if (wasDefault === shouldBeDefault) return view;
      return { ...view, isDefault: shouldBeDefault };
    });

    // Persist to IndexedDB with plain objects (MobX observables can't be cloned)
    try {
      for (const view of updatedViews) {
        const original = views.find((v) => v.id === view.id);
        if (original && original.isDefault !== view.isDefault) {
          const plain = JSON.parse(JSON.stringify(view)) as SavedView;
          await this.savedViewsTable.put(plain);
        }
      }
    } catch (e) {
      console.warn('[UiSavedViewsStore] Failed to update default view:', e);
      throw e;
    }

    runInAction(() => {
      this.viewsBySourceId[sourceId] = updatedViews;
    });
  }

  getDefaultView(sourceId: string): SavedView | undefined {
    return this.getViews(sourceId).find((v) => v.isDefault);
  }

  clearActiveView(sourceId: string) {
    runInAction(() => {
      this.activeViewId[sourceId] = null;
    });
  }
}
